import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { User, MapPin, MusicNote, Lock, Trophy, SpinnerGap, Sparkle } from '@phosphor-icons/react';
import LevelFigureModal from '../components/LevelFigureModal';
import { useLang, pickLang } from '../contexts/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAB_CONFIG = {
  all: { icon: Sparkle, i18nKey: 'all' },
  personalities: { icon: User, i18nKey: 'personalities' },
  cities: { icon: MapPin, i18nKey: 'cities' },
  songs: { icon: MusicNote, i18nKey: 'songs' },
};

const ICON_BY_TYPE = { personality: User, city: MapPin, song: MusicNote };

const AlbumCard = ({ item, onClick }) => {
  const { lang, t } = useLang();
  const Icon = ICON_BY_TYPE[item.type] || TAB_CONFIG[item._tabKey]?.icon || User;
  const locked = !item.unlocked;
  const fieldText = pickLang(item, 'field', lang) || pickLang(item, 'region', lang);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative rounded-2xl overflow-hidden border transition-all text-left ${
        locked
          ? 'bg-stone-100 border-stone-200 hover:border-stone-300 cursor-pointer'
          : 'bg-white border-[#D6D3D1] hover:shadow-lg hover:-translate-y-0.5 cursor-pointer'
      }`}
      data-testid={`album-card-${item.level}`}
    >
      <div
        className={`relative h-20 flex items-center justify-center ${locked ? 'bg-stone-300' : ''}`}
        style={locked ? {} : { background: `linear-gradient(135deg, ${item.color}, ${item.color}CC)` }}
      >
        <div className={`w-14 h-14 rounded-full flex items-center justify-center font-heading font-bold text-2xl shadow-md ${
          locked ? 'bg-stone-200 text-stone-400' : 'bg-white'
        }`}
          style={locked ? {} : { color: item.color }}
        >
          {locked ? <Lock size={24} weight="fill" /> : (item.name || '?').charAt(0).toUpperCase()}
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-center gap-1.5 mb-1">
          <Icon size={12} className={locked ? 'text-stone-400' : 'text-[#78716C]'} weight="fill" />
          <span className={`text-[10px] uppercase tracking-wider font-medium ${locked ? 'text-stone-400' : 'text-[#78716C]'}`}>
            {t('modal.level')} {item.level}
          </span>
        </div>
        <h3 className={`font-heading font-semibold text-sm truncate ${locked ? 'text-stone-500' : 'text-[#292524]'}`}>
          {locked ? `${t('album.locked')} (${item.min_points.toLocaleString(lang === 'de' ? 'de-DE' : 'es-ES')} Pkt)` : item.name}
        </h3>
        {!locked && fieldText && (
          <p className="text-xs text-[#78716C] truncate mt-0.5">{fieldText}</p>
        )}
      </div>
    </button>
  );
};

const AlbumPage = () => {
  const { lang, t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [modalLevel, setModalLevel] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/album`, { withCredentials: true });
        setData(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCardClick = (item) => {
    if (!item.unlocked) {
      const typeEs = t(`album.type.${item.type}`);
      const typeDe = { personality: 'Eintrag', city: 'Ort', song: 'Lied' }[item.type] || 'Eintrag';
      setModalLevel({
        level: item.level,
        name: `${t('modal.level')} ${item.level}`,
        min_points: item.min_points,
        color: '#a8a29e',
        type: item.type,
        bio_es: `Alcanza ${item.min_points.toLocaleString('es-ES')} puntos para desbloquear este ${typeEs}.`,
        bio_de: `Erreiche ${item.min_points.toLocaleString('de-DE')} Punkte, um diesen ${typeDe} freizuschalten.`,
      });
    } else {
      setModalLevel(item);
    }
    setModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <SpinnerGap size={32} className="animate-spin text-[#AB1C24]" />
      </div>
    );
  }

  if (!data) return <p className="text-center py-12 text-[#78716C]">—</p>;

  const items = (data.albums[activeTab] || []).map(i => ({ ...i, _tabKey: activeTab }));
  const unlocked = data.unlocked_counts[activeTab] || 0;
  const total = data.totals[activeTab] || 0;

  return (
    <div className="space-y-6" data-testid="album-page">
      <div className="bg-gradient-to-br from-[#AB1C24] to-[#8A151C] rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <Trophy size={28} weight="fill" className="text-[#EAB308]" />
            <span className="text-sm font-medium tracking-wider uppercase opacity-90">{t('album.header.pre')}</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">{t('album.header.title')}</h1>
          <p className="text-white/85 max-w-2xl">{t('album.header.sub')}</p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-[#D6D3D1] overflow-x-auto">
        {Object.entries(TAB_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          const active = activeTab === key;
          const u = data.unlocked_counts[key] || 0;
          const tt = data.totals[key] || 0;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium transition-colors whitespace-nowrap ${
                active ? 'border-[#AB1C24] text-[#AB1C24]' : 'border-transparent text-[#78716C] hover:text-[#292524]'
              }`}
              data-testid={`album-tab-${key}`}
            >
              <Icon size={18} weight={active ? 'fill' : 'regular'} />
              <span>{t(`album.tab.${cfg.i18nKey}`)}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${active ? 'bg-[#AB1C24]/10' : 'bg-stone-100'}`}>
                {u}/{tt}
              </span>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-[#D6D3D1] p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[#292524]">
            {t('album.progress.unlocked', { label: t(`album.tab.${TAB_CONFIG[activeTab].i18nKey}`) })}
          </span>
          <span className="text-sm font-bold text-[#AB1C24]">{unlocked} / {total}</span>
        </div>
        <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#AB1C24] to-[#EAB308] rounded-full transition-all duration-500"
            style={{ width: `${total ? (unlocked / total) * 100 : 0}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
        {items.map((item) => (
          <AlbumCard key={item.level} item={item} onClick={() => handleCardClick(item)} />
        ))}
      </div>

      <LevelFigureModal
        levelInfo={modalLevel}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

export default AlbumPage;
